<script>
  import { Spinner } from "agnostic-svelte"
  import { startCase } from "lodash"
  import { getDisplay, getInNetwork, getBackground } from "../../utils"
  import Ex from "./Ex.svelte"
  export let plan_promise
  export let show_prices = true
</script>

{#await plan_promise}
  <Spinner />
{:then plan}
  <div class="flex flex-column p8 round border " style="gap: 1em">
    <h2>{plan.name}</h2>
    <div class="p8 round border">
      {plan.raw?.issuer?.name}
      <hr />
      <a href={plan?.raw?.issuer?.shop_url || undefined} target="_blank"
        >Issuer</a
      >
    </div>
    {#if show_prices}
      <div class="p8 round border">
        <b>${plan.premium}</b>
        <hr />
        Premium
      </div>
    {/if}

    <div style="text-align:left">
      {#if show_prices}
        <Ex name="Premium" value={`$${plan.premium}/month`}>
          Your premium is the amount you pay for health insurance each month
        </Ex>
        <Ex
          name="Deductible"
          value={`$${plan.deductible}/year, ${plan.raw.deductibles.length} total`}
        >
          <div class="m4">
            Your deductible is the total amount you'll pay for healthcare
            services before your insurance starts to cover a portion of costs.
            (Scroll down to see the specific portion this plan covers for each
            service.)
          </div>
          <div class="m4">
            However, many plans do cover basic services even before the
            deductible has been met, on day one.
          </div>
          <div class="m4">
            <strong>Note: </strong>
            Monthly premiums don't count towards the deductible totals, and the deductible
            totals reset each year.
          </div>
          <ul>
            {#each plan.raw.deductibles as deductible}
              <li>
                {`${deductible.type}, ${deductible.family_cost}, $${deductible.amount}, ${deductible.network_tier}`}
              </li>
            {/each}
          </ul>
        </Ex>
        <Ex
          name="Out-of-pocket max"
          value={`$${plan.pocket_max}/person, ${plan.raw.moops.length} total`}
        >
          <div class="m4">
            This is the most you'll have to pay for healthcare services during
            your coverage period (typically 12 months) - think of it as your
            safety net in the event of a major health problem.
          </div>
          <div class="m4">
            <strong>Note: </strong>
            Monthly premiums don't count towards your out-of-pocket max.
          </div>
          <ul>
            {#each plan.raw.moops as moop}
              <li>
                {`${moop.type}, ${moop.family_cost}, $${moop.amount}, ${moop.network_tier}`}
              </li>
            {/each}
          </ul>
        </Ex>
      {/if}
      <Ex name="Network type" value={plan.network_type}>
        The type of network you choose (HMO, PPO, POS, or EPO) will determine
        which providers (doctors, pharmacies, hospitals, and specialist) you can
        see.
      </Ex>
      <Ex name="Metal tier" value={plan.metal_tier}>
        Metal levels (which include bronze, silver, gold, and platinum)
        determine how you will split the cost of your healthcare services with
        your insurance carrier.
      </Ex>
      <Ex
        name="Quality Rating"
        value={`${
          plan.raw.quality_rating?.available
            ? "Available ✅"
            : "Not Available ❌"
        }`}
      >
        <table>
          <tr>
            <th>Year</th>
            <td>{plan.raw.quality_rating?.year}</td>
          </tr>
          {#each ["global", "enrollee_experience", "plan_efficiency", "clinical_quality_management"] as item}
            <tr>
              <th>{startCase(item)} Rating</th>
              <td
                >{plan.raw.quality_rating?.[`${item}_rating`] ||
                  `Not rated, ${
                    plan.raw.quality_rating?.[`${item}_not_rated_reason`]
                  }`}</td
              >
            </tr>
          {/each}
        </table>
      </Ex>
      <Ex
        name="Disease Management Programs"
        value={`${plan.raw.disease_mgmt_programs.length} total`}
      >
        <ul>
          {#each plan.raw.disease_mgmt_programs as program}
            <li>{program}</li>
          {/each}
        </ul>
      </Ex>
      <Ex name="SBCS" value={`${Object.entries(plan.raw.sbcs).length} total`}>
        <ul>
          {#each Object.entries(plan.raw.sbcs) as [k, v]}
            <li>
              {startCase(k)}:
              {#each Object.entries(v) as [k, v], i}
                {i ? "," : ""} ${v} {startCase(k)}
              {/each}
            </li>
          {/each}
        </ul>
      </Ex>
      <Ex
        name="Benefits"
        value={`${
          plan.benefits.filter(
            (v) => v.network_in?.display_string === "No Charge"
          ).length
        } free in-network, ${
          plan.benefits.filter((v) => v.covered && !v.has_limits).length
        } covered, ${
          plan.benefits.filter((v) => v.covered && v.has_limits).length
        } covered with limits, ${plan.benefits.length} total`}
      >
        <div style="text-align:center; color: grey;margin:auto;max-width:300px; margin-bottom:16px;"><b>Summaries</b> display data for <b>in-network</b> coverage only. To see all details, expand the benefit by clicking on it.
        </div>
        {#each plan.benefits as benefit}
          <details>
            <summary
              >
              {benefit.name}
              {benefit.covered ? (benefit.has_limits ? " ❗" : " ✅") : " ❌"}
              <span
                style:background={getBackground(
                  benefit.network_in?.display_string
                )}
                style="padding-inline:8px; margin-left:8px; border-radius: 8px"
              >
                {getDisplay(benefit.network_in?.display_string || "")}
              </span>
            </summary>
            <p>
              {benefit.covered
                ? benefit.has_limits
                  ? "Covered with limits"
                  : "Covered"
                : "Not covered"}
            </p>
            <p>
              {benefit.exclusions
                ? "Exclusions: " + benefit.exclusions
                : "No Exclusions"}
            </p>
            <p>
              {benefit.explanation
                ? "Explanation: " + benefit.explanation
                : "No Explanation"}
            </p>
            Cost sharings

            <hr />
            <table>
              <tr>
                <th>In Network</th>
                <th>Out of Network</th>
              </tr>
              <tr>
                <td> {benefit.network_in?.display_string}</td>
                <td>
                  {benefit.network_out?.display_string}
                </td>
              </tr>
            </table>
          </details>
        {/each}
      </Ex>
    </div>
    <div class="p8 round border">
      <div class="flex" style="gap: 1em;justify-content:center">
        {#each Object.entries(plan.urls) as [name, url]}
          <a
            style="display:inline-block; padding:.7em;border-radius:10px;border:1px solid"
            target="_blank"
            href={url}>{startCase(name)}</a
          >
        {/each}
      </div>
      <hr />
      Further Reading
    </div>
    <slot />
  </div>
{:catch err}
  <code style="display: inline-block;">marketplace.api.healthcare.gov</code>
  <p>threw this error when trying to fetch plan:</p>
  <code style="white-space: pre;display: inline-block;text-align: left;"
    >{JSON.stringify(err, undefined, 2)}</code
  >
{/await}
